/* eslint-disable max-lines-per-function */
import React from 'react'
import { Button, Checkbox, FormControlLabel, Grid, MenuItem, TextField } from '@mui/material'
import { State, StateMethods } from '@hookstate/core'
import { useLang } from '~/hooks/useLang'
import { LoadingButton } from '@mui/lab'
import dayjs from 'dayjs'
import FilterAutocompleteStations from '~/components/FilterAutocompleteStations'
import FilterAutocompleteAgency from '~/components/FilterAutocompleteAgency'
import FilterAutocompleteContactCenter from '~/components/FilterAutocompleteContactCenter'
import FilterAutocompleteStationAuthenticated from '~/components/FilterAutocompleteBoolean'
import FilterAutocompleteProcedure from '~/components/FilterAutocompleteProcedure'
import FilterAutocompleteType from '~/components/FilterAutocompleteType'
import FilterAutocompleteSubtypesByType from '~/components/FilterAutocompleteSubtypeByType'
import FilterAutocompleteProcedureConfigRelated from '~/components/FilterAutocompleteProcedureConfigRelated'
import FilterAutocompleteAreaPlan from '~/components/FilterAutocompleteAreaPlan'
import { InputSearchWithClear } from '~/components/InputSearchWithClear'
import FilterAutocompleteDispatchGroup from '~/components/FilterAutocompleteDispatchGroup'
import FilterAutocompleteDeviceType from '~/components/FilterAutocompleteDeviceType'
import FilterAutocompleteSpecie from '~/components/FilterAutocompleteSpecie'
import FilterAutocompleteOperationUnit from '~/components/FilterAutocompleteOperationUnit'
import FilterAutocompletePermissionGroup from '~/components/FilterAutocompletePermissionGroup'
import FilterAutocompleteDevice from '~/components/FilterAutocompleteDevice'
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration'
import useDispatchRegistrationVehicleState from '~/features/Dispatch/stores/DispatchRegistrationVehicleState'
import FilterAutocompleteCity from '~/components/FilterSelectAutocompleteCity'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import FilterSelectMoment from '~/components/FilterAutocompleteMoment'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriodCrundIndex'
import FilterAutocompleteWorkShiftType from '~/components/FilterAutocompleteWorkShiftType'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterAutocompleteUnitMeasurement from '~/components/FilterAutocompleteUnitMeasurement'
import FilterAutocompleteItemsStatusInspectic from '~/components/FilterAutocompleteItemsStatusInspectic'
import FilterAutocompleteFetchData from '~/components/FilterAutocompleteDataData'
import FilterAutocompleteWeaponType from '~/components/FilterAutocompleteWeaponType'
import FilterAutocompleteEquipmentGroup from '~/components/FilterAutocompleteEquipmentGroup'
import FilterAutocompleteMaterialGroup from '~/components/FilterAutocompleteMaterialGroup'
import FilterAutocompleteWarehouse from '~/components/FilterAutocompleteWarehouse'
import FilterAutocompleteInspectionItem from '~/components/FilterAutocompleteInspectionItem'
import FilterAutocompleteEquipment from '~/components/FilterAutocompleteEquipment'
import CrudCheckBoxField from '~/components/CrudGeneratorFields/components/CrudCheckBoxField'
import FilterautocompleteWarehouseCheckoutStatus from '~/components/FilterautocompleteWarehouseCheckoutStatus'
import FilterAutocompleteMaterial from '~/components/FilterautocompleteMaterials'

export interface FilterArrayProps {
  readonly name?: string
  readonly type: string
  readonly variable: State<any> | StateMethods<any>
  readonly dependent?: State<any> | StateMethods<any> | readonly (State<any> | StateMethods<any>)[]
  readonly placeholder?: string
  readonly gridSize?: number
  readonly disabled?: boolean;
  readonly sendToRequest?: boolean
  readonly groupset?: State<any> | StateMethods<any>
  readonly options?: readonly (string | number)[]
}

export interface FiltersDashboardProps {
  readonly loading?: State<boolean>
  readonly lockFields?: boolean
  readonly disbaledSearchButton: State<boolean> | undefined
  readonly searchButton?: () => void
  readonly clearFilters?: () => void
  readonly filters: readonly FilterArrayProps[] | undefined
}

export default function FiltersDashboard({
  loading,
  lockFields,
  clearFilters,
  searchButton,
  filters,
  disbaledSearchButton,
}: FiltersDashboardProps) {
  const { translate } = useLang()
  const { getVehicleTypes } = useDispatchRegistrationVehicleState()


  function showFilters(type, variable, dependent, placeholder, groupset, disabled, options) {
    const safeGet = () =>
      typeof variable?.get === 'function'
        ? variable.get()
        : variable?.value ?? variable

    const safeSet = (valueUpdater: (currentValue: any) => any) => {
      if (typeof variable?.set === 'function') {
        variable.set(valueUpdater(safeGet() ?? {}))
      }
    }

    switch (type) {
      case 'period': {
        const currentValue = safeGet() ?? {}
        return (
          <FilterSelectPeriod
            disabled={lockFields}
            period={currentValue.period ?? ''}
            startDateValue={currentValue.startDate ?? null}
            endDateValue={currentValue.endDate ?? null}
            onPeriodChange={(periodValue) =>
              safeSet((current) => ({ ...current, period: periodValue }))
            }
            onStartDateChange={(startDateValue) =>
              safeSet((current) => ({
                ...current,
                startDate: startDateValue?.toJSON() ?? null,
              }))
            }
            onFinishDateChange={(finishDateValue) =>
              safeSet((current) => ({
                ...current,
                endDate: finishDateValue?.toJSON() ?? null,
              }))
            }
          />
        )
      }
      case 'hidden':
        return null
      case 'station_ids':
        return (
          <FilterAutocompleteStations
            stationsId={variable.get()}
            onStationsChange={(station) => variable.set(station)}
            disabled={lockFields}
          />
        )
      case 'agency_id':
        return (
          <FilterAutocompleteAgency
            agencyId={variable.get()}
            onAgencyChange={(agency) => variable.set(agency)}
            disabled={lockFields}
          />
        )
      case 'contact_center_id':
        return (
          <FilterAutocompleteContactCenter
            contactCenterId={variable.get()}
            onContactCenterChange={(contactCenter) => variable.set(contactCenter)}
            disabled={lockFields}
          />
        )
      case 'is_authenticated':
        return (
          <FilterAutocompleteStationAuthenticated
            label={'Authenticated Stations'}
            isChecked={variable.get()}
            onIsCheckedChange={(isAuthenticated) => variable.set(isAuthenticated)}
            disabled={lockFields}
          />
        )
      case 'procedure_id':
        return (
          <FilterAutocompleteProcedure
            procedureId={variable.get()}
            onProcedureChange={(procedure) => variable.set(procedure)}
            size={'small'}
          />
        )
      case 'type_id':
        return (
          <FilterAutocompleteType
            typeId={variable.get()}
            onTypeChange={(type) => variable.set(Number(type?.id) ?? null)}
          />
        )
      case 'subtype_id':
        return (
          <FilterAutocompleteSubtypesByType
            hasType
            typeId={dependent ? dependent.get() : null}
            subtypeId={variable.get()}
            onSubtypeChange={(subtype) => variable.set(subtype?.id ?? null)}
          />
        )
      case 'related_to':
        return (
          <FilterAutocompleteProcedureConfigRelated
            procedureConfigRelatedId={variable.get()}
            onProcedureConfigRelatedChange={(relatedTo) => variable.set(relatedTo)}
            disabled={lockFields}
          />
        )
      case 'area_plan_id':
        return (
          <FilterAutocompleteAreaPlan
            onAreaPlanChange={(areaPlan) => variable.set(areaPlan)}
            areaPlanId={variable.get()}
            disabled={lockFields}
          />
        )
      case 'name':
        return (
          <InputSearchWithClear
            search={variable}
            placeholder={translate(placeholder ?? 'Name')}
            sx={{ width: '100%', height: '100%' }}
          />
        )
      case 'dispatch_group_id':
        return (
          <FilterAutocompleteDispatchGroup
            disabled={lockFields}
            dispatchGroupId={variable.get() ?? undefined}
            onGroupDispatchChange={(group) => variable.set(group)}
            agencyId={dependent ? dependent.get() : null}
          />
        )
      case 'tracking_id':
        return (
          <InputSearchWithClear
            search={variable}
            placeholder={translate(placeholder)}
            sx={{ width: '100%', height: '100%' }}
          />
        )
      case 'code':
        return (
          <InputSearchWithClear
            search={variable}
            placeholder={translate(placeholder ?? 'Code')}
            sx={{ width: '100%', height: '100%' }}
          />
        )
      case 'material_group_id':
        return (
          <FilterAutocompleteMaterialGroup
            materialGroupId={variable.get()}
            onMaterialGroupChange={(materialGroup) => variable.set(materialGroup)}
            disabled={lockFields}
          />
        )
      case 'measurement_unit_id':
        return (
          <FilterAutocompleteUnitMeasurement
            unitMeasurementId={variable.get() ?? undefined}
            onUnitMeasurementChange={(value: number | null) => variable.set(value)}
            disabled={lockFields}
          />
        )
      case 'city_id':
        return (
          <FilterAutocompleteCity
            cityId={variable.get()}
            onCityChange={(city) => variable.set(city)}
            disabled={lockFields}
          />
        )
      case 'device_id':
        return (
          <FilterAutocompleteDevice
            deviceId={variable.get()}
            onDeviceChange={(deviceType) => variable.set(deviceType)}
            disabled={lockFields}
          />
        )
      case 'device_type_id':
        return (
          <FilterAutocompleteDeviceType
            deviceTypeId={variable.get()}
            onDeviceTypeChange={(deviceType) => variable.set(deviceType)}
            disabled={lockFields}
          />
        )
      case 'work_shift_id':
        return (
          <FilterAutocompleteWorkShiftType
            workShiftTypeId={variable.get()}
            onWorkShiftTypeChange={(workShiftType) => variable.set(workShiftType?.id)}
            disabled={lockFields}
          />
        )
      case 'vehicle_type_id':
        return (
          <FieldAutocompleteRegistration
            valueId={(variable.value?.id ?? variable.value) as string}
            isRequired
            optionsFetch={getVehicleTypes}
            label={translate('Vehicle Type')}
            onValueChange={(value) => variable.set(value?.id)}
            disabled={lockFields}
          />
        )
      case 'user_id':
        return (
          <FilterAutocompleteFetch
            label={translate('User')}
            urlFetch={'/user/get'}
            dataRequest={'user'}
            size={'small'}
            disabled={false}
            onFetchChange={(user: number | null) => variable?.set(user)}
            valueId={variable?.get() ? Number(variable?.get()) : undefined}
          />
        )
      case 'active':
        return (
          <FilterAutocompleteStationAuthenticated
            label={'Actived'}
            isChecked={variable.get()}
            onIsCheckedChange={(isActive) =>
              variable.set(isActive == null ? isActive : isActive == 1 ? true : false)
            }
            disabled={lockFields}
            isBooleanField
          />
        )
      case 'item_status':
        return (
          <FilterAutocompleteItemsStatusInspectic
            label={'Situação'}
            isChecked={variable.get()}
            onIsCheckedChange={(isOperational) => variable.set(isOperational)}
            disabled={lockFields}
          />
        )
      case 'specie_id':
        return (
          <FilterAutocompleteSpecie
            onSpecieChange={(specie) => variable.set(specie)}
            specieId={variable.get()}
            disabled={lockFields}
          />
        )
      case 'operation_unit_id':
        return (
          <FilterAutocompleteOperationUnit
            onOperationUnitChange={(operationUnit) => variable.set(operationUnit)}
            operationUnitId={variable.get()}
            disabled={lockFields}
          />
        )
      case 'group_id':
        return (
          <FilterAutocompletePermissionGroup
            onGroupChange={(group) => variable.set(group)}
            groupId={variable.get()}
            disabled={lockFields}
          />
        )
      case 'usual':
        return (
          <FilterAutocompleteStationAuthenticated
            label={'Usual'}
            isChecked={variable.get()}
            onIsCheckedChange={(isActive) =>
              variable.set(isActive == null ? isActive : isActive == 1 ? true : false)
            }
            disabled={lockFields}
            isBooleanField
          />
        )
      case 'group_set_id':
        return (
          <FilterAutocompleteDispatchGroupSet
            disabled={lockFields}
            dispatchGroupSetId={variable.value ?? undefined}
            onGroupDispatchSetChange={(group) => variable.set(group)}
          />
        )
      case 'start_date':
        return (
          <FilterSelectMoment
            onDateChange={(value) => variable.set(value?.toJSON() ?? null)}
            disabled={disabled || lockFields}
            date={variable.get()}
            label={'Initial Date'}
            disableFuture={false}
            maxDate={dependent?.get() ? dayjs(dependent.get()) : null}
          />
        )
      case 'finish_date':
        return (
          <FilterSelectMoment
            onDateChange={(value) => variable.set(value?.toJSON() ?? null)}
            disabled={disabled || lockFields}
            date={variable.get()}
            label={'End Date'}
            disableFuture={false}
            minDate={dependent?.get() ? dayjs(dependent.get()) : null}
          />
        )
      case 'initial_date':
        return (
          <FilterSelectMoment
            onDateChange={(value) => variable.set(value?.toJSON() ?? null)}
            disabled={lockFields}
            date={variable.get()}
            label={'Initial Date'}
          />
        )
      case 'boolean':
        return (
          <FilterAutocompleteStationAuthenticated
            label={translate(placeholder ?? 'Active')}
            isChecked={variable.get()}
            onIsCheckedChange={(value) => variable.set(value)}
            disabled={lockFields}
          />
        )
      case 'priority':
        return (
          <TextField
            type="number"
            size="small"
            label={translate(placeholder ?? 'Priority')}
            value={variable.get() ?? ''}
            onChange={(event) => {
              const value = event.target.value
              variable.set(value === '' ? null : Number(value))
            }}
            disabled={lockFields}
            inputProps={{ min: 1, max: 9 }}
            fullWidth
          />
        )
      case 'weapon_type_id':
        return (
          <FilterAutocompleteWeaponType
            weaponTypeId={variable.get()}
            onWeaponTypeChange={(weaponType) => variable.set(weaponType)}
            disabled={lockFields}
          />
        )
      case 'equipment_group_id':
        return (
          <FilterAutocompleteEquipmentGroup
            equipmentGroupId={variable.get()}
            onEquipmentGroupChange={(equipmentGroup) => variable.set(equipmentGroup)}
            disabled={lockFields}
          />
        )
      case 'equipment_id':
        return (
          <FilterAutocompleteFetch
            label={translate('Equipment')}
            urlFetch={'/equipment/all-for-logged-user'}
            dataRequest={'equipment'}
            paramsPayload={{ warehouse_id: dependent.get() }}
            size={'small'}
            onFetchChange={(id: number | null) => variable.set(id)}
            valueId={variable.get() ?? undefined}
          />
        )
      case 'material_id':
        return (
          <FilterAutocompleteFetch
            label={translate('Materials')}
            urlFetch={'/warehouse-material/all-for-logged-user'}
            dataRequest={'material'}
            paramsPayload={{ warehouse_id: dependent.get() }}
            size={'small'}
            onFetchChange={(id: number | null) => variable.set(id)}
            valueId={variable.get() ?? undefined}
          />
        )
      case 'warehouse_id':
        return (
          <FilterAutocompleteWarehouse
            warehouseId={variable.get()}
            onWarehouseChange={(warehouse) => variable.set(warehouse)}
            disabled={lockFields}
            warehouseByUser={dependent?.get()}
          />
        )
        case 'inspection_item_id':
        return (
          <FilterAutocompleteInspectionItem
            inspectionItemId={variable.get()}
            onInspectionItemChange={(item) => variable.set(item)}
            disabled={lockFields}
          />
        )
      case 'warehouseStatus_id':
        return (
          <FilterautocompleteWarehouseCheckoutStatus
            onWarehouseStatusChange={(warehouseStatus) => variable.set(warehouseStatus)}
            warehouseStautsId={variable.get()}
            disabled={lockFields}
          />
        )
      case 'boolean_check':
        return (
          <FormControlLabel
            label={placeholder}
            control={
              <Checkbox
                checked={variable.get() ?? false}
                onChange={(event, value) => variable.set(Boolean(value))}
              />
            }
          />
        )
      case 'select':
        return (
          <TextField
            select
            size="small"
            label={translate(placeholder ?? 'Select')}
            value={variable.get() ?? ''}
            onChange={(event) => {
              const value = event.target.value
              variable.set(value === '' ? null : Number(value))
            }}
            disabled={lockFields}
            fullWidth
          >
            <MenuItem value="">
              <em>
                {translate('None')}
              </em>
            </MenuItem>
            {options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        )
      default:
        return null
    }
  }

  return (
    <Grid xs={12} container display={'flex'} flexDirection={'row'} alignItems={'flex-start'} paddingBottom={3}>
      <Grid container item xs={10} spacing={1} >
        {filters?.map((item, index) => {

          const element = showFilters(
            item.type,
            item.variable,
            item.dependent,
            item.placeholder,
            item.groupset,
            item.disabled,
            item.options,
          )

          if (!element) {
            return null
          }

          return (
            <Grid
              item
              xs={10}
              md={item.gridSize ?? 3}
              key={`${item.name ?? item.type} -${index} `}
            >
              {element}
            </Grid>
          )
        })}
      </Grid>

      <Grid container item xs={2} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={1}>
        <Button variant="outlined" onClick={clearFilters}>
          {translate('Clear Filters')}
        </Button>
        <LoadingButton
          disabled={disbaledSearchButton?.get()}
          loading={loading?.get()}
          variant="contained"
          onClick={searchButton}
        >
          {translate('Search')}
        </LoadingButton>
      </Grid>
    </Grid >
  )
}
